"use client";

import { useMemo } from "react";

import type { QRProps } from "@/contexts/QREditorContext/types";
import { usePreviewData } from "@/features/previews/shared/hooks/usePreviewData";
import { usePreviewValidation } from "@/features/previews/shared/hooks/usePreviewValidation";

import type { SmsTemplateData, SmsTemplateTranslations, UseSmsDataResult } from "../types";
import {
  DEFAULT_SMS_AVATAR,
  DEFAULT_SMS_CONTACT_NAME,
  clampMessage,
  formatPhoneNumber,
  isValidSmsContent,
} from "../utils";

export const useSmsData = (props: QRProps<"sms">): UseSmsDataResult => {
  const { data, isInitPreview, context } = usePreviewData(props);
  const smsData = data as SmsTemplateData | null;

  const translations = props.previewTranslations?.["sms"] as SmsTemplateTranslations | undefined;

  usePreviewValidation({
    rules: [
      {
        type: "custom",
        validator: () => isValidSmsContent(smsData ?? undefined),
      },
    ],
    dependencies: [smsData?.phoneNumber, smsData?.message],
    context,
    isInitPreview,
  });

  const { displayName, profileImage, message } = useMemo(() => {
    if (!smsData) {
      return {
        displayName: DEFAULT_SMS_CONTACT_NAME,
        profileImage: DEFAULT_SMS_AVATAR,
        message: "",
      };
    }

    const formattedPhone = formatPhoneNumber(smsData.phoneNumber);

    const resolvedName = isInitPreview
      ? smsData.profileName || DEFAULT_SMS_CONTACT_NAME
      : formattedPhone || DEFAULT_SMS_CONTACT_NAME;

    return {
      displayName: resolvedName,
      profileImage: smsData.profileImage || DEFAULT_SMS_AVATAR,
      message: clampMessage(smsData.message),
    };
  }, [smsData, isInitPreview]);

  return {
    translations: translations ?? {},
    isInitPreview,
    data: smsData,
    displayName,
    profileImage,
    message,
  };
};
